import { TDbAccount } from "../dbAccount";
import { LinkUtils } from "../newTypes/Link";
import { ParamUtils } from "../newTypes/Param";
import { ILightUser } from "./lightProfile";

export interface ILightNestedUser {
  id: string;
  SIGNATURE: string;
  parentGroup: string;
  originalObjectSIG: string;
  address: string;
  public_key: string[];
  name: string;
  isDeleted: boolean;
  isValid: boolean;
}

export const LightNestedUserUtils = {
  create({
    address,
    id,
    SIGNATURE,
    parentGroup,
    originalObjectSIG,
    name = "",
    public_key = [],
    isDeleted,
    isValid,
  }: {
    id: string;
    SIGNATURE: string;
    parentGroup: string;
    originalObjectSIG: string;
    address: string;
    public_key?: string[];
    name?: string;
    isDeleted: boolean;
    isValid: boolean;
  }): ILightNestedUser {
    return {
      id,
      SIGNATURE,
      parentGroup,
      originalObjectSIG,
      address,
      name,
      public_key,
      isDeleted,
      isValid,
    };
  },
  empty(): ILightNestedUser {
    return LightNestedUserUtils.create({
      id: "",
      SIGNATURE: "",
      parentGroup: "",
      originalObjectSIG: "",
      address: "",
      name: "",
      public_key: [],
      isDeleted: false,
      isValid: false,
    });
  },

  fromMap(map: Record<string, any>): ILightNestedUser {
    try {
      const object = map["object"] ?? {};

      const id = map["id"] ?? "";
      const SIGNATURE = object["SIGNATURE"] ?? "";
      const parentGroup = object["parentGroup"] ?? "";
      const originalObjectSIG = object["originalObjectSIG"] ?? "";

      const link =
        map["Link"] || map["link"]
          ? LinkUtils.fromMap((map["Link"] || map["link"]) as Record<string, any>)
          : LinkUtils.empty();

      const profileParam =
        object["profile"] && object["profile"] !== ""
          ? (object["profile"] as any[]).map((p) => ParamUtils.fromMap(p))
          : [];
      const userNameParam = profileParam.find(
        (param: { key: string; value: any }) => param.key === "user_name"
      );
      if (!userNameParam) {
        console.error("User name parameter not found in profile:", profileParam);
        return LightNestedUserUtils.empty();
      }
      const userName = userNameParam.value || "";

      const publicKeyRaw = object["public_key"];

      let address: string;
      let public_keys: string[];
      if (typeof publicKeyRaw === "string") {
        address = publicKeyRaw;
        public_keys = [publicKeyRaw];
      } else if (Array.isArray(publicKeyRaw)) {
        address =
          (publicKeyRaw as string[]).length > 0 ? publicKeyRaw[publicKeyRaw.length - 1] : link.id;
        public_keys = publicKeyRaw as string[];
      } else {
        address = link.id;
        public_keys = [link.id];
      }
      return LightNestedUserUtils.create({
        id: id,
        SIGNATURE: SIGNATURE,
        parentGroup: parentGroup,
        originalObjectSIG: originalObjectSIG,
        address: address,
        name: userName,
        public_key: public_keys.map((key: string) => String(key)),
        isDeleted: !!map["isDeleted"],
        isValid: !!map["isValid"],
      });
    } catch (e) {
      console.error("Error creating ILightNestedUser from map:", e);
      return LightNestedUserUtils.empty();
    }
  },

  fromJson(source: string): ILightNestedUser {
    try {
      return LightNestedUserUtils.fromMap(JSON.parse(source));
    } catch (e) {
      console.error("Error parsing NestedUser from JSON:", e);
      return LightNestedUserUtils.empty();
    }
  },
  toMap(nestedUser: ILightNestedUser): Record<string, any> {
    return {
      address: nestedUser.address,
      public_key: nestedUser.public_key,
      name: nestedUser.name,
      isDeleted: nestedUser.isDeleted,
      isValid: nestedUser.isValid,
    };
  },
  toJson(nestedUser: ILightNestedUser): string {
    return JSON.stringify(LightNestedUserUtils.toMap(nestedUser));
  },

  createFromDbAccount(dbAccount: TDbAccount): ILightNestedUser {
    // some of the fields are hard coded. this method create only for ui purpose!
    return LightNestedUserUtils.create({
      id: "",
      SIGNATURE: "",
      parentGroup: "",
      originalObjectSIG: "",
      address: dbAccount.public_key,
      name: dbAccount.name,
      public_key: [dbAccount.public_key],
      isDeleted: false, // hard coded
      isValid: true, // hard coded
    });
  },

  createFromLightUser(lightUser: ILightUser): ILightNestedUser {
    // some of the fields are hard coded. this method create only for ui purpose!
    return LightNestedUserUtils.create({
      id: "",
      SIGNATURE: "",
      parentGroup: "",
      originalObjectSIG: "",
      address: lightUser.address,
      name: lightUser.name,
      public_key: [lightUser.address],
      isDeleted: false, // hard coded
      isValid: true, // hard coded
    });
  },
  copyWith(
    nestedUser: ILightNestedUser,
    {
      address,
      id,
      SIGNATURE,
      parentGroup,
      originalObjectSIG,
      public_key,
      name,
      isDeleted,
      isValid,
    }: {
      address?: string;
      id?: string;
      SIGNATURE?: string;
      parentGroup?: string;
      originalObjectSIG?: string;
      public_key?: string[];
      name?: string;
      isDeleted?: boolean;
      isValid?: boolean;
    }
  ): ILightNestedUser {
    return LightNestedUserUtils.create({
      id: id ?? nestedUser.id,
      SIGNATURE: SIGNATURE ?? nestedUser.SIGNATURE,
      parentGroup: parentGroup ?? nestedUser.parentGroup,
      originalObjectSIG: originalObjectSIG ?? nestedUser.originalObjectSIG,
      address: address ?? nestedUser.address,
      name: name ?? nestedUser.name,
      public_key: public_key ?? nestedUser.public_key,
      isDeleted: isDeleted ?? nestedUser.isDeleted,
      isValid: isValid ?? nestedUser.isValid,
    });
  },
};
